import React, { useState } from 'react';
import InterviewProctoringContent from '../../pages/results/components/InterviewProctoringContent';
import {
  ALL_VIOLATION_TYPES,
  getRiskBadgeFromScore,
  getViolationTypePoints,
  getTotalRiskPointsFromDetails,
  getDisplayRiskScore,
  formatViolationTypeLabel,
  getViolationTypeDescription,
} from './proctoringUtils';

/**
 * Renders the "Proctoring Details" tab content for the Candidate Results modal.
 * Handles loading, error, and success. Expects session shape from
 * GET /api/proctoring/sessions (session object with violations, riskScore, etc.).
 *
 * For multi-module assessments, `session` is an array of per-module session objects.
 * In that case, module tabs are rendered (mirroring the Assessment Score tab layout).
 *
 * @param {Object} props
 * @param {Object|Array|null} props.session - Single session object, array for multi-module,
 *   or { error: true, message } or null
 * @param {Object} [props.candidate] - { name?, email?, proctoringRiskScore? } for display and risk fallback
 */
function CandidateProctoringDetailsContent({ session, candidate = {} }) {
  const [activeModuleIdx, setActiveModuleIdx] = useState(0);

  // Multi-module: session is an array — render tabs then delegate to single-session view.
  if (Array.isArray(session)) {
    const modules = session;
    const safeIdx = Math.min(activeModuleIdx, modules.length - 1);
    const activeSession = modules[safeIdx] || null;
    return (
      <div className="space-y-4">
        {/* Module tabs — same style as Assessment Score modal */}
        <div className="flex flex-wrap gap-2">
          {modules.map((mod, i) => {
            const label = mod.moduleTitle || `Module ${i + 1}`;
            const hasVideoProctoring = !!mod.videoProctoringResult?.analyzedAt;
            const risk = hasVideoProctoring
              ? (mod.riskScore ?? null)
              : (typeof getTotalRiskPointsFromDetails(mod) === 'number'
                ? getTotalRiskPointsFromDetails(mod)
                : null);
            const active = safeIdx === i;
            return (
              <button
                key={mod.sessionId || i}
                onClick={() => setActiveModuleIdx(i)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                  active
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {label}
                {risk != null ? ` · ${risk} pts` : ''}
              </button>
            );
          })}
        </div>
        {/* Active module's session rendered by the single-session view below */}
        <SingleSessionView session={activeSession} candidate={candidate} />
      </div>
    );
  }
  // Single session view — also used by the multi-module tab renderer above.
  return <SingleSessionView session={session} candidate={candidate} />;
}

function SingleSessionView({ session, candidate = {} }) {
  const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-md bg-gray-200/90 ${className}`} />
  );

  if (session?.notAttempted) {
    return (
        <div className="py-16 text-center text-gray-500">
        <p className="text-base font-semibold text-red-600">Candidate not attempted.</p>
      </div>
    );
  }

  if (session?.error) {
    return (
      <div className="py-16 text-center text-red-600">
        {session.message ?? 'Failed to load proctoring details.'}
      </div>
    );
  }

  if (!session) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <SkeletonBlock className="h-5 w-36" />
              <SkeletonBlock className="h-8 w-28 rounded-lg" />
              <SkeletonBlock className="h-5 w-32" />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <SkeletonBlock className="h-4 w-24" />
              <SkeletonBlock className="h-4 w-24" />
              <SkeletonBlock className="h-4 w-24" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-5 py-5 space-y-2">
              <SkeletonBlock className="h-6 w-40" />
              <SkeletonBlock className="h-4 w-56" />
            </div>
            <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100 space-y-3">
              <SkeletonBlock className="h-4 w-24" />
              <SkeletonBlock className="h-8 w-28 rounded-lg" />
              <div className="pt-2 border-t border-gray-200 flex flex-wrap gap-2">
                <SkeletonBlock className="h-3.5 w-24" />
                <SkeletonBlock className="h-3.5 w-24" />
                <SkeletonBlock className="h-3.5 w-24" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm h-fit">
            <div className="px-5 py-5">
              <SkeletonBlock className="h-6 w-44" />
            </div>
            <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100 space-y-3">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={`proctoring-row-skeleton-${idx}`} className="flex items-center justify-between">
                  <SkeletonBlock className="h-4 w-36" />
                  <SkeletonBlock className="h-4 w-10" />
                </div>
              ))}
              <div className="pt-3 border-t border-gray-200">
                <SkeletonBlock className="h-4 w-48" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (session?.videoProctoringResult?.analyzedAt) {
    return <InterviewProctoringContent data={session} session={session} context="assessment" />;
  }

  const displayScore =
    (candidate.assessmentProctoringRiskScore ?? candidate.proctoringRiskScore) ??
    getDisplayRiskScore(session) ??
    session.riskScore;
  const badge = getRiskBadgeFromScore(displayScore);
  const scoreText =
    displayScore !== undefined && displayScore !== null
      ? displayScore > 100
        ? ` · ${displayScore}+`
        : ` · ${displayScore}`
      : '';
  const byType = session.violations?.byType || {};
  const entriesWithCount = Object.entries(byType).filter(([, count]) => Number(count) > 0);
  const allRows =
    entriesWithCount.length > 0
      ? entriesWithCount
      : Object.keys(byType).length > 0
        ? Object.entries(byType)
        : ALL_VIOLATION_TYPES.map((t) => [t, 0]);
  const sorted = allRows
    .map(([type, count]) => [type, count, getViolationTypePoints(type, count)])
    .sort((a, b) => b[2] - a[2]);
  const top4 = sorted.slice(0, 4);
  const used = new Set(top4.map(([t]) => t));
  const padding = ALL_VIOLATION_TYPES.filter((t) => !used.has(t))
    .slice(0, 4 - top4.length)
    .map((t) => [t, 0]);
  const tableRows = [...top4.map(([t, c]) => [t, c]), ...padding];
  const totalPoints = getTotalRiskPointsFromDetails(session);
  const isFlagged = displayScore != null && Number(displayScore) >= 50;

  return (
    <div className="space-y-4">
      {/* Top Summary (Decision Key) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-sm font-semibold text-gray-900">Proctoring Summary</span>
            {displayScore != null ? (
              <span className={`inline-flex px-3 py-1.5 text-sm font-semibold rounded-lg ${badge.classes}`}>
                {badge.label}
                {scoreText}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
            <span className="text-sm text-gray-700">
              <span className="font-semibold">Decision:</span>{' '}
              <span className={isFlagged ? 'font-semibold text-orange-700' : 'font-semibold text-green-700'}>
                {isFlagged ? 'Flagged' : 'Not flagged'}
              </span>
            </span>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-gray-600">Compliant (0-29)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="text-gray-600">Flagged (30-70)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-gray-600">High Risk (71+)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-5 py-5">
            <p className="text-lg font-semibold text-gray-900">
              {candidate.name ?? session.candidateName ?? 'Anonymous'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {candidate.email ?? session.candidateEmail ?? ''}
            </p>
          </div>
          <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Risk Summary
            </h4>
            {displayScore != null ? (
              <span
                className={`inline-flex px-3 py-1.5 text-sm font-semibold rounded-lg ${badge.classes}`}
              >
                {badge.label}
                {scoreText}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-gray-600">Compliant (0-29)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-orange-500" />
                <span className="text-gray-600">Flagged (30-70)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-gray-600">High Risk (71+)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm h-fit">
        {session.violations ? (
          <>
            <div className="px-5 py-5">
              <p className="text-lg font-semibold text-gray-900">Violations Summary</p>
            </div>
            <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-100 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="pr-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type of Violations
                    </th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="pl-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Total Points
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tableRows.map(([type, count]) => {
                    const n = Number(count);
                    const subtotal = getViolationTypePoints(type, count);
                    return (
                      <tr key={type}>
                        <td className="pr-4 py-2.5 text-sm font-medium text-gray-900">
                          {formatViolationTypeLabel(type)}
                        </td>
                        <td className="px-2 py-2.5 text-sm text-gray-600 text-right">{n}</td>
                        <td className="pl-4 py-2.5 text-sm font-medium text-gray-900 text-right">
                          {subtotal}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200">
                    <td className="pr-4 py-2.5 text-sm font-semibold text-gray-800">
                      Total risk points
                    </td>
                    <td className="px-2 py-2.5" />
                    <td className="pl-4 py-2.5 text-sm font-semibold text-gray-900 text-right">
                      {totalPoints}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-[10px] text-gray-500 mb-1.5">
                  Points per occurrence (hover for description):
                </p>
                <div className="flex flex-wrap gap-x-2 gap-y-1 text-[10px]">
                  {tableRows.map(([t]) => {
                    const { pts, desc } = getViolationTypeDescription(t);
                    return (
                      <span
                        key={t}
                        title={desc}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 transition-colors"
                      >
                        <span className="font-medium text-gray-700">
                          {formatViolationTypeLabel(t)}
                        </span>
                        <span className="mx-1 text-gray-400">·</span>
                        <span>{pts} pts</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-5 py-5">
            <p className="text-sm text-gray-500">No violations data available</p>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

export default CandidateProctoringDetailsContent;
